/**
 * Canonical merge for creator profile tab rows (contract:
 * creator-tabs-system-markers v3). Single source of the two invariants shared
 * by the studio manager and the consumer profile:
 *
 * 1. Server row order is NEVER rearranged — a creator's persisted mix of
 *    manual tabs and system markers renders verbatim.
 * 2. Known default sections missing from the response are synthesized and
 *    PREPENDED in canonical order. A channel that never reordered therefore
 *    renders defaults-first, pixel-identical to the pre-markers UI.
 *
 * Unknown `type` values (newer BE than this FE) are skipped — bucketing them
 * as manual tabs would render broken rows.
 */

import { SYSTEM_TAB_TYPES, type SystemTabType } from '@/types/studio-tabs';

/**
 * FE-synthesized placeholder for a default section with no DB row yet.
 * `uuid: null` is the discriminant — reorder payloads must reference these by
 * `type_marker`, never by uuid.
 */
export interface SyntheticSystemTab {
  type: SystemTabType;
  uuid: null;
  system_marker: true;
  display_order: null;
}

/**
 * FE-owned labels for the default sections — the contract says server `title`
 * on marker rows is informational only; both studio and consumer render these.
 */
export const SYSTEM_TAB_LABELS: Record<SystemTabType, string> = {
  videos: 'Videos',
  shorts: 'Shorts',
  posts: 'Posts',
  education: 'Education',
  series: 'Series',
};

interface TabRowLike {
  type: string;
}

export function isSystemTabType(type: string): type is SystemTabType {
  return (SYSTEM_TAB_TYPES as readonly string[]).includes(type);
}

export function isSyntheticSystemTab(row: {
  uuid: string | null;
}): row is { uuid: null } & SyntheticSystemTab {
  return row.uuid === null;
}

export function mergeCreatorTabs<T extends TabRowLike>(
  rows: readonly T[] | undefined
): Array<SyntheticSystemTab | T> {
  const known = (rows ?? []).filter((row) => row.type === 'manual' || isSystemTabType(row.type));

  const presentTypes = new Set(known.map((row) => row.type));
  const missing: SyntheticSystemTab[] = SYSTEM_TAB_TYPES.filter(
    (key) => !presentTypes.has(key)
  ).map((type) => ({ type, uuid: null, system_marker: true, display_order: null }));

  return [...missing, ...known];
}
